import React, { useMemo, useState } from "react";
import { TooltipText } from "../../../CommonComponents";

import { UserOutlined, CopyOutlined, LinkOutlined, ClockCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { copyToClipboard } from "../../../utils/helper";
import { Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import "./ParentAdminGroups.css";
import { useQuery } from "@tanstack/react-query";
import logger from "../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { countries } from "countries-list";
import useToken from "../../../hooks/useToken";
import { chantApi } from "../../../utils/chantApi";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm";

const useParentGroups = () => {
  const navigate = useNavigate();
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const { token } = useToken();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(null);

  const fetchGroups = async () => {
    try {
      const response = await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/group/parent/${groupData?.id}/children`,
        { parentId: groupData?.id }
      );

      return response;
    } catch (error) {
      logger.error("Error fetching parent admin groups", error);
      return [];
    }
  };

  const {
    data: parentAdminGroups,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["parentAdminGroups", groupData?.id, token],
    queryFn: fetchGroups,
    enabled: !!token && !!groupData?.id,
  });

  const allGroups = useMemo(() => {
    return Object.values(parentAdminGroups?.data?.children || {}) || [];
  }, [parentAdminGroups]);

  // Get unique countries for filter dropdown
  const uniqueCountries = useMemo(() => {
    const countryCodes = new Set();
    allGroups.forEach((group) => {
      if (group.supportersCountry) {
        countryCodes.add(group.supportersCountry);
      }
    });
    return Array.from(countryCodes)
      .map((code) => ({
        value: code,
        label: countries?.[code]?.name || code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allGroups]);

  // Filter groups based on search term and country
  const filteredGroups = useMemo(() => {
    let filtered = [...allGroups];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((group) =>
        group.groupName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by country
    if (selectedCountry) {
      filtered = filtered.filter(
        (group) => group.supportersCountry === selectedCountry
      );
    }

    return filtered;
  }, [allGroups, searchTerm, selectedCountry]);

  const totalCount = useMemo(() => {
    return filteredGroups.length;
  }, [filteredGroups]);

  const handleSendConnectionRequest = async (groupId) => {
    try {
      setSendingRequest(groupId);
      await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/group/parent/${groupData?.id}/connection-request`,
        { groupId, parentId: groupData?.id }
      );
      message.success("Connection request sent successfully.");
      refetch();
    } catch (error) {
      logger.error("Error sending connection request", error);
      message.error("Failed to send connection request. Please try again.");
    } finally {
      setSendingRequest(null);
    }
  };

  const columns = [
    {
      title: "Action",
      width: 100,
      render: (data) => (
        <div className="flex-center" style={{ gap: "12px" }}>
          <Tooltip title="View Members">
            <UserOutlined
              onClick={() => navigate(`/clubadmin/${data.id}/members`)}
            />
          </Tooltip>
          {data.connectionRequest?.status === "accepted" ? (
            <Tooltip title="Connected">
              <CheckCircleOutlined style={{ color: "#52c41a", cursor: "default" }} />
            </Tooltip>
          ) : data.connectionRequest?.status === "pending" ? (
            <Tooltip title="Request Pending">
              <ClockCircleOutlined style={{ color: "#faad14", cursor: "default" }} />
            </Tooltip>
          ) : (
            <ChantPopConfirm
              title={`Send connection request to ${data.groupName}?`}
              onConfirm={() => handleSendConnectionRequest(data.id)}
              onCancel={() => {}}
              okText="Send"
              cancelText="Cancel"
            >
              <Tooltip title="Send Connection Request">
                <LinkOutlined
                  style={{
                    opacity: sendingRequest === data.id ? 0.5 : 1,
                    cursor: sendingRequest === data.id ? "not-allowed" : "pointer",
                  }}
                />
              </Tooltip>
            </ChantPopConfirm>
          )}
        </div>
      ),
    },
    {
      title: "Group",
      width: 200,
      key: "groupName",
      dataIndex: "groupName",
      sorter: (a, b) => (a.groupName || "").localeCompare(b.groupName || ""),
      render: (groupName) => {
        return <span>{groupName}</span>;
      },
    },
    {
      title: "Country",
      width: 200,
      key: "supportersCountry",
      dataIndex: "supportersCountry",
      render: (countryCode) => (
        <span>
          {countries?.[countryCode]?.name || countryCode || "Unknown"}
        </span>
      ),
    },
    {
      title: "Admin(s)",
      width: window.screen.width > 500 ? 200 : 100,
      key: "admins",
      dataIndex: "admins",
      render: (admins) => {
        if (!admins || !Array.isArray(admins) || admins.length === 0) {
          return <span>-</span>;
        }

        const handleCopyEmail = (admin, e) => {
          e.stopPropagation();
          if (admin.email) {
            copyToClipboard(admin.email, `${admin.name}'s email copied!`);
          } else {
            copyToClipboard(admin.name, "Admin name copied!");
          }
        };

        const renderAdminName = (admin, showComma = false) => (
          <React.Fragment key={admin.email || admin.name}>
            <Tooltip
              title={
                admin.email
                  ? `Click to copy: ${admin.email}`
                  : "Click to copy name"
              }
              placement="top"
              mouseEnterDelay={0.3}
            >
              <span
                onClick={(e) => {
                  handleCopyEmail(admin, e);
                }}
                style={{
                  color: "#1890ff",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  transition: "text-decoration 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                {admin.name}
                <CopyOutlined style={{ fontSize: "12px" }} />
              </span>
            </Tooltip>
            {showComma && ", "}
          </React.Fragment>
        );

        const displayLimit = 2;
        const visibleAdmins = admins.slice(0, displayLimit);
        const remainingCount = admins.length - displayLimit;

        if (admins.length <= displayLimit) {
          return (
            <span>
              {admins.map((admin, index) =>
                renderAdminName(admin, index < admins.length - 1)
              )}
            </span>
          );
        }

        const tooltipContent = (
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {admins.map((admin, index) => (
              <div
                key={index}
                onClick={(e) => handleCopyEmail(admin, e)}
                style={{
                  padding: "4px 8px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(24, 144, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <CopyOutlined
                  style={{ marginRight: "8px", fontSize: "12px" }}
                />
                {admin.name}
                {admin.email && (
                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.8,
                      marginLeft: "20px",
                    }}
                  >
                    {admin.email}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

        return (
          <span>
            {visibleAdmins.map((admin, index) => renderAdminName(admin, index < visibleAdmins.length - 1))}
            <Tooltip title={tooltipContent} placement="topLeft">
              <span
                style={{
                  color: "#1890ff",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                +{remainingCount} more
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: <TooltipText title={`Registered Members`} maxWidth={145} />,
      key: "regMembers",
      className: "header-white",
      dataIndex: "registeredMembers",
      sorter: (a, b) => (a.registeredMembers || 0) - (b.registeredMembers || 0),
      render: (regMembers) => <span>{regMembers || 0}</span>,
      width: 150,
    },
    {
      title: <TooltipText title={`Total Members`} maxWidth={145} />,
      key: "totalMembers",
      className: "header-white",
      dataIndex: "lastMembershipCount",
      sorter: (a, b) =>
        (a.lastMembershipCount || 0) - (b.lastMembershipCount || 0),
      render: (lastMembershipCount) => <span>{lastMembershipCount || 0}</span>,
      width: 150,
    },
    {
      title: <TooltipText title={`% Registered`} maxWidth={145} />,
      key: "percentReg",
      className: "header-white",
      sorter: (a, b) => {
        const percentA =
          a?.lastMembershipCount && a?.lastMembershipCount > 0
            ? (a?.registeredMembers / a?.lastMembershipCount) * 100
            : 100;
        const percentB =
          b?.lastMembershipCount && b?.lastMembershipCount > 0
            ? (b?.registeredMembers / b?.lastMembershipCount) * 100
            : 100;
        return percentA - percentB;
      },
      render: (_, record) => (
        <>
          {record?.lastMembershipCount && record?.lastMembershipCount > 0
            ? Math.round(
                (record?.registeredMembers / record?.lastMembershipCount) * 100
              )
            : 100}
        </>
      ),
      width: 150,
    },
  ];

  const handleDownloadCsv = () => {
    try {
      setDownloading(true);

      const headers = [
        "Group Name",
        "Country",
        "Admins",
        "Registered Members",
        "Total Members",
        "% Registered",
      ];

      // Prepare CSV rows
      const rows = filteredGroups.map((group) => {
        // Format admins with bullet points and line breaks for Excel
        const admins =
          group.admins && group.admins.length > 0
            ? group.admins
                .map(
                  (admin) =>
                    `• ${admin.name}${admin.email ? ` (${admin.email})` : ""}`
                )
                .join("\n")
            : "-";

        const countryName =
          countries?.[group.supportersCountry]?.name ||
          group.supportersCountry ||
          "Unknown";

        const percentRegistered =
          group?.lastMembershipCount && group?.lastMembershipCount > 0
            ? Math.round(
                (group?.registeredMembers / group?.lastMembershipCount) * 100
              )
            : 100;

        return [
          `"${group.groupName || ""}"`,
          `"${countryName}"`,
          `"${admins.replace(/"/g, '""')}"`, // Escape quotes and wrap in quotes for multiline
          group.registeredMembers || 0,
          group.lastMembershipCount || 0,
          percentRegistered,
        ].join(",");
      });

      const csv = [headers.join(","), ...rows].join("\n");

      const a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(
        new Blob([csv], { type: "text/csv" })
      );
      a.download = `${groupData?.groupName || "groups"}_children_groups.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      logger.error("Failed to download groups list: ", error);
    } finally {
      setDownloading(false);
    }
  };

  return {
    searchTerm,
    selectedCountry,
    filteredGroups,
    isLoading,
    refetch,
    uniqueCountries,
    totalCount,
    columns,
    handleDownloadCsv,
    setSearchTerm,
    setSelectedCountry,
    downloading,
  };
};

export default useParentGroups;
